import { useCallback, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import type { RequestCompilationBody, RequestCompilationResponse } from "../../client-messages/compilation";
import { requestCompilationMutation } from "../queries";

export interface RequestCompilationParams {
  readonly siteId: SiteId;
  readonly assetCameraKey: AssetCameraKey;
  readonly body: RequestCompilationBody;
}

export interface RequestCompilationResult {
  /**
   * Fire the POST for the given camera. Unlike `useRequestTimelapseVideo`
   * (fixed to the camera the page is already scoped to), `POST /compilation`
   * is requested from a tenant-wide page where the camera is chosen inside
   * the form — so the camera travels with each call instead of being fixed
   * at hook-mount time. Invalidates the compilations list on settle so it
   * reflects the new job.
   */
  readonly requestCompilation: (params: RequestCompilationParams) => Promise<RequestCompilationResponse>;
  readonly isPending: boolean;
  readonly error: Error | null;
}

/** Mutation hook for `POST /compilation`. */
export function useRequestCompilation(): RequestCompilationResult {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationConfig = useMemo(
    () =>
      apiConfig.tenant ?
        {
          tenantId: apiConfig.tenant.tenantId,
          apiBaseUrl: apiConfig.tenant.apiBaseUrl,
          authenticatedFetch: apiConfig.authenticatedFetch,
        }
      : { authenticatedFetch: apiConfig.authenticatedFetch },
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const mutation = useMutation({
    mutationFn: ({ siteId, assetCameraKey, body }: RequestCompilationParams) =>
      requestCompilationMutation(mutationConfig, { siteId, assetCameraKey }).mutationFn(body),
    onSettled: () => {
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "compilations" });
    },
  });

  const mutateAsyncRef = useRef(mutation.mutateAsync);
  mutateAsyncRef.current = mutation.mutateAsync;
  const requestCompilation = useCallback((params: RequestCompilationParams) => mutateAsyncRef.current(params), []);

  return {
    requestCompilation,
    isPending: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
  };
}
